import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  CommandDashboardKeys,
  CommandDashboardTaskIds,
  CommandDashboardTaskStatuses,
  getGroupsAndAccessTileMetrics,
  refreshGroupsAndAccessTileMetrics,
} from '../../api/command-dashboard-api';
import { handleErrors } from '../../utils/errors';
import { Messages } from '../../utils/message-constants';
import { useStore } from '../state/zustand-store';
import {
  isCommandDashboardRefreshAlreadyRunning,
  useCommandDashboardTileTaskStatus,
} from './use-command-dashboard-tile-task-status';
import { useTrackCommandDashboardTileRefresh } from './use-tile-refresh';

export function useGroupsAndAccessTileMetrics(enabled = true) {
  const trackRefresh = useTrackCommandDashboardTileRefresh();
  const { setSnackbar } = useStore();
  const queryClient = useQueryClient();
  // Whether a refresh is running lives in the backend's TaskStatus rows, not in this hook - so it
  // survives a reload, and is already correct for a refresh someone else (or the nightly
  // dispatch) started. Fetched here rather than page-level so it is only requested when a
  // task-backed tile is actually on the grid.
  const { statusQuery, isRefreshing } = useCommandDashboardTileTaskStatus(
    CommandDashboardTaskIds.GroupsAndAccess,
    CommandDashboardKeys.GroupsAndAccessTile,
    enabled
  );

  const query = useQuery({
    queryKey: [CommandDashboardKeys.GroupsAndAccessTile],
    queryFn: getGroupsAndAccessTileMetrics,
    enabled,
    placeholderData: keepPreviousData,
    meta: {
      errorMessage: Messages.CommandDashboard.GroupsAndAccessTileLoadError,
    },
  });

  const onRefresh = async () => {
    const previousStatuses =
      queryClient.getQueryData<CommandDashboardTaskStatuses>([
        CommandDashboardKeys.TaskStatuses,
      ]);
    queryClient.setQueryData<CommandDashboardTaskStatuses>(
      [CommandDashboardKeys.TaskStatuses],
      (old = {}) => ({
        ...old,
        [CommandDashboardTaskIds.GroupsAndAccess]: 'RUNNING',
      })
    );
    try {
      await refreshGroupsAndAccessTileMetrics();
      trackRefresh();
      setSnackbar({
        open: true,
        severity: 'info',
        message: Messages.CommandDashboard.GroupsAndAccessTileRefreshStarted,
      });
    } catch (error) {
      if (isCommandDashboardRefreshAlreadyRunning(error)) {
        // The conflict means a run is in flight - keep the optimistic RUNNING row so Refresh
        // stays disabled, and wait for that task's completion the same way as a kick-off we
        // started ourselves.
        queryClient.setQueryData<CommandDashboardTaskStatuses>(
          [CommandDashboardKeys.TaskStatuses],
          (old = {}) => ({
            ...old,
            [CommandDashboardTaskIds.GroupsAndAccess]: 'RUNNING',
          })
        );
        setSnackbar({
          open: true,
          severity: 'info',
          message:
            Messages.CommandDashboard.GroupsAndAccessTileRefreshAlreadyRunning,
        });
        return;
      }
      queryClient.setQueryData<CommandDashboardTaskStatuses>(
        [CommandDashboardKeys.TaskStatuses],
        (current = {}) => {
          const restored = { ...current };
          const previousStatus =
            previousStatuses?.[CommandDashboardTaskIds.GroupsAndAccess];
          if (previousStatus) {
            restored[CommandDashboardTaskIds.GroupsAndAccess] = previousStatus;
          } else {
            delete restored[CommandDashboardTaskIds.GroupsAndAccess];
          }
          return restored;
        }
      );
      handleErrors({
        error,
        snackbarMessage:
          Messages.CommandDashboard.GroupsAndAccessTileRefreshError,
      });
    }
  };

  return {
    metrics: query.data,
    lastUpdated: query.data?.lastUpdated ?? null,
    // The status map is part of this tile's initial load, so the Refresh control is never drawn
    // from a status that has not arrived yet.
    isLoading: query.isLoading || statusQuery.isLoading,
    isFetching: query.isFetching || statusQuery.isLoading,
    isRefreshing,
    onRefresh,
  };
}
